import { TriggerSource } from "@repo/api/schemas/policiesInventory.schema";
import "./StickyFooter.css";

import {
  Box,
  Button,
  FooterActionBar,
  Link,
  Typography,
} from "@saviynt/design-system";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHostViewport } from "@repo/mfe-core/host";
import { useFindingsData } from "../../pages/PoliciesFindingsList/context/FindingsDataContext";
import { useSelection } from "../../pages/PoliciesFindingsList/context/SelectionContext";
import TooManyItemsSelectedModal from "../../pages/PoliciesFindingsList/Modals/TooManyItemsSelectedModal";

interface StickyFooterProps {
  variant: TriggerSource;
  maxSelection: number;
  primaryButtonText: string;
  onPrimaryAction: () => void;
  hideSelectAllLink: boolean;
  setHideSelectAllLink: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
}

export default function StickyFooter({
  variant,
  maxSelection,
  primaryButtonText,
  onPrimaryAction,
  hideSelectAllLink,
  setHideSelectAllLink,
  className = "",
}: StickyFooterProps) {
  const intl = useIntl();
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [selectionText, setSelectionText] = useState("");
  const { selectedRows, clearSelection } = useSelection();
  const { recordsCount } = useFindingsData();
  const { sidenavWidth } = useHostViewport();

  const config: Record<
    TriggerSource,
    {
      maxSelectionAllowed: number;
      footerDataTestId: string;
      primaryBtnDataTestId: string;
    }
  > = {
    findings: {
      maxSelectionAllowed: 1000,
      footerDataTestId: "NHI-Findings-Sticky-Footer",
      primaryBtnDataTestId: "NHI-Findings-Footer-CreateTicket-Button",
    },
    inventory: {
      maxSelectionAllowed: 100,
      footerDataTestId: "NHI-Inventory-Sticky-Footer",
      primaryBtnDataTestId: "NHI-Inventory-Footer-AssignOwner-Button",
    },
    policies: {
      maxSelectionAllowed: 100,
      footerDataTestId: "NHI-Policies-Sticky-Footer",
      primaryBtnDataTestId: "NHI-Policies-Footer-Action-Button",
    },
  };

  const { maxSelectionAllowed, footerDataTestId, primaryBtnDataTestId } =
    config[variant];

  useEffect(() => {
    if (hideSelectAllLink) {
      setSelectionText(
        intl.formatMessage(
          {
            defaultMessage: "Selected: all {count} items on this page",
            description: "Text showing all items are selected in the footer",
            id: "nEQrsD",
          },
          { count: recordsCount.totalRecordsCount },
        ),
      );
    } else {
      setSelectionText(
        intl.formatMessage(
          {
            defaultMessage: "Selected: {count} items on this page",
            description:
              "Text showing the number of selected items in the footer",
            id: "XXsw7V",
          },
          { count: selectedRows.length },
        ),
      );
    }
  }, [
    hideSelectAllLink,
    intl,
    selectedRows.length,
    recordsCount.totalRecordsCount,
  ]);

  if (!selectedRows.length) {
    return null;
  }

  const clickHeaderCheckbox = () => {
    // Select the checkbox inside the wrapper using a partial class match
    const checkbox = document.querySelector(
      "[class*='TableHeadItem-labelIconWrapper_v'] button[role='checkbox']",
    ) as HTMLButtonElement | null;

    if (checkbox) {
      checkbox.click();
    }

    return null; // nothing rendered
  };

  const handlePrimaryAction = () => {
    if (selectedRows.length > maxSelection) {
      setIsErrorOpen(true);
    } else {
      onPrimaryAction();
    }
  };

  const handleSelectAll = () => {
    if (recordsCount.totalRecordsCount > maxSelection) {
      setIsErrorOpen(true);
    } else {
      const checkbox = document.querySelector(
        "[class*='TableHeadItem-labelIconWrapper_v'] button[role='checkbox']",
      ) as HTMLButtonElement | null;

      // Check if header checkbox is in indeterminate or unchecked state
      const isAllVisibleSelected =
        checkbox?.getAttribute("aria-checked") === "true";

      // Only click if not all visible items are selected
      if (!isAllVisibleSelected) {
        clickHeaderCheckbox();
      }

      // Use setTimeout to ensure this runs after all other state updates
      setTimeout(() => {
        setHideSelectAllLink(true);
      }, 100);
    }
  };

  const handleClearSelection = () => {
    clearSelection();
    setHideSelectAllLink(false);
  };

  return (
    <div
      style={
        { "--nhi-footer-left": `${sidenavWidth}px` } as React.CSSProperties
      }
    >
      {isErrorOpen && (
        <TooManyItemsSelectedModal
          isOpen={isErrorOpen}
          setIsOpen={setIsErrorOpen}
          count={maxSelectionAllowed}
        />
      )}

      <FooterActionBar
        size="small"
        dataTheme="dark"
        className={`NHI-sticky-footer ${className}`}
        dataTestId={footerDataTestId}
      >
        <Box className="NHI-FooterActionBar-flexBox">
          <Box className="NHI-FooterActionBar-extraContent">
            <Typography
              kind="body2"
              desktop="body2-d"
              mobile="body2-d"
              color="--color-foreground-neutral-bold"
              dataTheme="dark"
            >
              {selectionText}
            </Typography>
            {!hideSelectAllLink && (
              <Link
                text={intl.formatMessage(
                  {
                    defaultMessage: "Select all {count}  Items",
                    description: "Link text to select all items",
                    id: "jvrPET",
                  },
                  { count: recordsCount.totalRecordsCount },
                )}
                color="brand"
                dataTheme="dark"
                kind="button"
                onClick={handleSelectAll}
              />
            )}
          </Box>
          <Box className="NHI-FooterActionBar-actionButtons">
            <Button
              kind="ghost"
              dataTheme="dark"
              onClick={handleClearSelection}
            >
              {intl.formatMessage({
                defaultMessage: "Clear Selection",
                description: "Clear Selection Button Text",
                id: "ur2nHv",
              })}
            </Button>

            <Box className="NHI-FooterActionBar-actionButtons--isPrimary">
              <Button
                kind="outlined"
                dataTheme="dark"
                onClick={handlePrimaryAction}
                dataTestId={primaryBtnDataTestId}
              >
                {primaryButtonText}
              </Button>
            </Box>
          </Box>
        </Box>
      </FooterActionBar>
    </div>
  );
}
